import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { adminService } from '../services/admin-service';
import type { RevisionDeBorrado } from '../types/admin.types';

/*
  Qué se muestra del resumen, con nombre legible. Solo aparecen las filas con
  algo adentro: una empresa vacía se dice en una línea y no con catorce ceros.
*/
const ETIQUETAS_DEL_RESUMEN: Array<[keyof RevisionDeBorrado['resumen'], string]> = [
  ['usuarios', 'cuentas de usuario'],
  ['invitaciones', 'invitaciones'],
  ['cotizaciones', 'cotizaciones'],
  ['productos', 'productos'],
  ['clientes', 'clientes'],
  ['modelosDePricing', 'modelos de pricing'],
  ['materiales', 'materiales'],
  ['ventas', 'ventas'],
  ['ordenesDeImportacion', 'órdenes de importación'],
  ['cotizacionesDatec', 'cotizaciones Datec'],
  ['cotizacionesSolares', 'cotizaciones solares'],
  ['tiendasCompetidoras', 'tiendas competidoras'],
  ['lecturasDeCatalogo', 'barridos del catálogo de la competencia'],
  ['vinculosConLaCompetencia', 'vínculos con la competencia'],
  ['preciosDeLaCompetenciaCargados', 'precios de la competencia cargados a mano'],
  ['contratos', 'contratos'],
];

/**
 * El diálogo de confirmación para borrar una empresa.
 *
 * No es un "¿seguro?": primero muestra qué tiene la empresa (un dry-run al
 * servidor), después pide escribir el slug a mano, y si todavía hay datos
 * adentro también pide tildar que se sabe que se borran. Cada candado que el
 * servidor impone —usuarios activos, admins de plataforma, datos sin
 * declarar— aparece acá como motivo, no como un error genérico.
 */
export function EliminarEmpresaDialog({
  empresa,
  onClose,
  onEliminada,
}: {
  empresa: { id: string; name: string; slug: string } | null;
  onClose: () => void;
  onEliminada: () => void;
}) {
  const [revision, setRevision] = useState<RevisionDeBorrado | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [confirmacion, setConfirmacion] = useState('');
  const [entiende, setEntiende] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!empresa) {
      setRevision(null);
      setConfirmacion('');
      setEntiende(false);
      setError(null);
      return;
    }
    let activo = true;
    setRevisando(true);
    setRevision(null);
    setConfirmacion('');
    setEntiende(false);
    setError(null);
    adminService
      .revisarBorradoDeEmpresa(empresa.id)
      .then((r) => {
        if (activo) setRevision(r);
      })
      .catch((err) => {
        if (activo) setError(err?.response?.data?.error ?? 'No se pudo revisar la empresa.');
      })
      .finally(() => {
        if (activo) setRevisando(false);
      });
    return () => {
      activo = false;
    };
  }, [empresa]);

  const filasDelResumen = revision
    ? ETIQUETAS_DEL_RESUMEN.filter(([clave]) => revision.resumen[clave] > 0).map(
        ([clave, etiqueta]) => ({ etiqueta, cantidad: revision.resumen[clave] })
      )
    : [];

  const hayContenido = filasDelResumen.length > 0;
  const bloqueada = !!revision && revision.bloqueos.length > 0;

  const puedeConfirmar =
    !!revision &&
    !revisando &&
    !bloqueada &&
    confirmacion === empresa?.slug &&
    (!hayContenido || entiende);

  const eliminar = async () => {
    if (!empresa || !puedeConfirmar) return;
    setBorrando(true);
    setError(null);
    try {
      await adminService.deleteTenant(empresa.id, {
        confirmation: confirmacion,
        acknowledgeData: hayContenido || undefined,
      });
      onEliminada();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'No se pudo eliminar la empresa.');
    } finally {
      setBorrando(false);
    }
  };

  return (
    <Dialog open={!!empresa} onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" aria-hidden="true" />
            Eliminar empresa
          </DialogTitle>
          <DialogDescription>
            {empresa ? (
              <>
                Esto borra <span className="font-medium">{empresa.name}</span> ({empresa.slug}) y
                todo lo que tiene adentro. No se puede deshacer.
              </>
            ) : (
              ' '
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {revisando && <p className="text-sm text-muted-foreground">Revisando la empresa…</p>}

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {revision && bloqueada && (
            <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
              <p className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" /> No se puede borrar todavía
              </p>
              <ul className="list-disc space-y-1 pl-6 text-sm text-destructive">
                {revision.bloqueos.map((bloqueo) => (
                  <li key={bloqueo}>{bloqueo}</li>
                ))}
              </ul>
            </div>
          )}

          {revision && !bloqueada && (
            <>
              {revision.personas && revision.personas.seBorran.length > 0 && (
                <div className="space-y-1 rounded-md border p-3">
                  <p className="text-sm font-medium">
                    {revision.liberaCorreos
                      ? 'Se borran estas cuentas y sus correos quedan libres para volver a darse de alta:'
                      : 'Se borran estas cuentas:'}
                  </p>
                  <ul className="space-y-0.5 text-sm text-muted-foreground">
                    {revision.personas.seBorran.map((p) => (
                      <li key={p.email}>
                        {p.nombre} · {p.email}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {revision.personas && revision.personas.siguenEnOtraEmpresa.length > 0 && (
                <div className="space-y-1 rounded-md border p-3">
                  <p className="text-sm font-medium">
                    Siguen en otra empresa (conservan su cuenta):
                  </p>
                  <ul className="space-y-0.5 text-sm text-muted-foreground">
                    {revision.personas.siguenEnOtraEmpresa.map((p) => (
                      <li key={p.email}>
                        {p.nombre} · {p.email} · {p.empresa}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {hayContenido ? (
                <div className="space-y-1 rounded-md border p-3">
                  <p className="text-sm font-medium">Esto también se borra:</p>
                  <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-muted-foreground">
                    {filasDelResumen.map((fila) => (
                      <li key={fila.etiqueta} className="flex justify-between gap-2">
                        <span>{fila.etiqueta}</span>
                        <span className="tabular-nums">{fila.cantidad}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  La empresa está vacía: no tiene usuarios ni datos.
                </p>
              )}

              {hayContenido && (
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={entiende}
                    onCheckedChange={(marcado) => setEntiende(marcado === true)}
                    className="mt-0.5"
                  />
                  <span>Entiendo que todo lo de arriba se va a borrar con la empresa.</span>
                </label>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="confirmacion-slug">
                  Escribí <span className="font-mono font-medium">{empresa?.slug}</span> para
                  confirmar
                </Label>
                <Input
                  id="confirmacion-slug"
                  value={confirmacion}
                  onChange={(e) => setConfirmacion(e.target.value)}
                  placeholder={empresa?.slug}
                  autoComplete="off"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={eliminar}
            disabled={!puedeConfirmar || borrando}
            aria-label={empresa ? `Eliminar ${empresa.name}` : 'Eliminar empresa'}
          >
            {borrando ? 'Eliminando…' : 'Eliminar empresa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
